import { gpHome } from '../../app';
import {
    ISubscriptionManagementApi,
    LicenseAssignmentRequest,
    LicenseSummary,
    LicenseTypeDefinition,
    PersonalSubscriptionUpsertRequest,
    SubscriptionManagementServiceName,
    SubscriptionSummary,
    TeamMemberInviteRequest,
    TeamMemberRoleUpdateRequest,
    TeamSummary,
    TeamUpsertRequest,
    SeatUpdateRequest,
} from '../../services/subscription-management-contract';
import subscriptionManagementTemplate from './subscription-management.html';

interface MemberDirectoryEntry {
    userId: string;
    displayName: string;
    teamId?: string;
    teamName?: string;
}

class SubscriptionManagementController {
    static $inject = ['$q', SubscriptionManagementServiceName];

    public isLoading = true;
    public errorMessage = '';

    public personalSubscriptions: SubscriptionSummary[] = [];
    public teams: TeamSummary[] = [];
    public licenses: LicenseSummary[] = [];
    public licenseTypes: LicenseTypeDefinition[] = [];
    public memberDirectory: MemberDirectoryEntry[] = [];
    public activeTab: 'personal' | 'business' | 'create-team' = 'personal';
    public selectedTeamId: string | null = null;
    public currentTeamRoleKeys: string[] = [];
    public canEditTeam = true;

    constructor(
        private readonly $q: ng.IQService,
        private readonly api: ISubscriptionManagementApi,
    ) {
        this.refreshAll();
    }

    refreshAll() {
        this.isLoading = true;
        this.errorMessage = '';
        this.$q.all({
            subs: this.api.getPersonalSubscriptions(),
            teams: this.api.getTeams(),
            licenses: this.api.getLicenses(),
            licenseTypes: this.api.getLicenseTypes(),
        })
            .then(result => {
                this.personalSubscriptions = result.subs;
                this.teams = result.teams;
                this.licenses = result.licenses;
                this.licenseTypes = result.licenseTypes;
                this.memberDirectory = this.buildDirectory(this.teams);
                if (!this.selectedTeamId && this.teams.length > 0) {
                    this.selectedTeamId = this.teams[0].id;
                }
                if (this.selectedTeamId) {
                    this.updateCurrentTeamRole(this.selectedTeamId);
                }
            })
            .catch(() => {
                this.errorMessage = 'Unable to load subscription data. This UI currently uses a mock API until the backend contract is implemented.';
            })
            .finally(() => this.isLoading = false);
    }

    refreshTeams() {
        return this.api.getTeams().then(teams => {
            this.teams = teams;
            this.memberDirectory = this.buildDirectory(teams);
            if (!this.selectedTeamId && teams.length > 0) {
                this.selectedTeamId = teams[0].id;
            }
            if (this.selectedTeamId) {
                this.updateCurrentTeamRole(this.selectedTeamId);
            }
        });
    }

    refreshLicenses() {
        return this.api.getLicenses().then(licenses => this.licenses = licenses);
    }

    refreshPersonal() {
        return this.api.getPersonalSubscriptions().then(subs => this.personalSubscriptions = subs);
    }

    upsertPersonalSubscription = (request: PersonalSubscriptionUpsertRequest) => {
        return this.api.upsertPersonalSubscription(request)
            .then(() => this.refreshPersonal())
            .then(() => this.refreshTeams())
            .then(() => this.refreshLicenses());
    };

    cancelPersonalSubscription = (subscriptionId: string) => {
        return this.api.cancelPersonalSubscription(subscriptionId)
            .then(() => this.refreshPersonal());
    };

    createTeam = (request: TeamUpsertRequest) => {
        return this.api.createTeam(request)
            .then(() => this.refreshTeams());
    };

    updateTeam = (teamId: string, request: TeamUpsertRequest) => {
        return this.api.updateTeam(teamId, request)
            .then(() => this.refreshTeams());
    };

    deleteTeam = (teamId: string) => {
        return this.api.deleteTeam(teamId)
            .then(() => this.refreshTeams());
    };

    inviteMember = (teamId: string, request: TeamMemberInviteRequest) => {
        return this.api.inviteTeamMember(teamId, request)
            .then(() => this.refreshTeams());
    };

    removeMember = (teamId: string, userId: string) => {
        return this.api.removeTeamMember(teamId, userId)
            .then(() => this.refreshTeams())
            .then(() => this.refreshLicenses());
    };

    updateMemberRole = (teamId: string, userId: string, request: TeamMemberRoleUpdateRequest) => {
        return this.api.updateTeamMemberRole(teamId, userId, request)
            .then(() => this.refreshTeams());
    };

    assignLicense = (request: LicenseAssignmentRequest) => {
        if (!request.userId) {
            return this.$q.resolve();
        }
        return this.api.assignLicense(request)
            .then(() => this.refreshLicenses())
            .then(() => this.refreshTeams());
    };

    unassignLicense = (licenseId: string) => {
        return this.api.unassignLicense(licenseId)
            .then(() => this.refreshLicenses())
            .then(() => this.refreshTeams());
    };

    updateSubscriptionSeats = (request: SeatUpdateRequest) => {
        return this.api.updateSubscriptionSeats(request)
            .then(() => this.refreshTeams());
    };

    linkSubscriptionToTeam = (request: { teamId: string; subscriptionId: string; seatCount?: number; }) => {
        return this.api.linkSubscriptionToTeam(request)
            .then(() => this.refreshTeams());
    };

    setActiveTab(tab: 'personal' | 'business') {
        this.activeTab = tab;
    }

    selectTeam(teamId: string) {
        this.selectedTeamId = teamId;
        this.updateCurrentTeamRole(teamId);
    }

    private updateCurrentTeamRole(teamId: string) {
        this.api.getCurrentUserTeamRole(teamId).then(info => {
            this.currentTeamRoleKeys = info.roleKeys;
            this.canEditTeam = info.canManage;
        }).catch(() => {
            this.currentTeamRoleKeys = [];
            this.canEditTeam = false;
        });
    }

    private buildDirectory(teams: TeamSummary[]): MemberDirectoryEntry[] {
        const entries: MemberDirectoryEntry[] = [];
        teams.forEach(team => {
            team.members.forEach(m => {
                entries.push({
                    userId: m.userId,
                    displayName: `${m.displayName || m.email} (${team.name})`,
                    teamId: team.id,
                    teamName: team.name,
                });
            });
        });
        return entries;
    }
}

gpHome.directive('gpSubscriptionManagement', [
    function () {
        return {
            restrict: 'E',
            scope: {},
            controller: SubscriptionManagementController,
            controllerAs: '$ctrl',
            bindToController: true,
            template: subscriptionManagementTemplate,
        };
    }
]);
